import consumer from "@/channels/consumer";
import type { Message, StatusUpdate } from "@/types/chat";
import { useCallback, useEffect, useRef } from "react";

type ChatChannelMessage = {
  chatRemoteJid: string;
  message: Message;
};

type ChatChannelStatusUpdate = {
  chatRemoteJid: string;
  statusUpdate: StatusUpdate;
};

type ChatChannelError = {
  error: string;
};

type ChatChannelData = ChatChannelMessage | ChatChannelStatusUpdate | ChatChannelError;

type UseChatChannelOptions = {
  onMessage?: (data: ChatChannelMessage) => void;
  onStatusUpdate?: (data: ChatChannelStatusUpdate) => void;
  onError?: (error: string) => void;
};

export function useChatChannel({ onMessage, onStatusUpdate, onError }: UseChatChannelOptions) {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onStatusUpdateRef = useRef(onStatusUpdate);
  onStatusUpdateRef.current = onStatusUpdate;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const subscriptionRef = useRef<ReturnType<
    typeof consumer.subscriptions.create
  > | null>(null);

  useEffect(() => {
    const subscription = consumer.subscriptions.create("ChatChannel", {
      received(data: ChatChannelData) {
        if ("error" in data) {
          onErrorRef.current?.(data.error);
        } else if ("statusUpdate" in data) {
          onStatusUpdateRef.current?.(data);
        } else {
          onMessageRef.current?.(data);
        }
      },
    });
    subscriptionRef.current = subscription;

    return () => {
      subscription.unsubscribe();
      subscriptionRef.current = null;
    };
  }, []);

  const sendMessage = useCallback((contactId: number, text: string) => {
    subscriptionRef.current?.perform("send_message", {
      contact_id: contactId,
      text,
    });
  }, []);

  const markRead = useCallback((remoteJid: string) => {
    subscriptionRef.current?.perform("mark_read", {
      remote_jid: remoteJid,
    });
  }, []);

  return { sendMessage, markRead };
}
